/**
 * NPC response boundary — the backend's real shapes, unwrapped once.
 *
 * Every NPC endpoint answers `{ success, data }`, but `data` is not always the
 * value the web wants:
 *
 *   GET  /npc/personas/:id/levels  → data = { persona, levels: Level[] }
 *        (npcController.getPersonaLevels). The web used to hand the whole
 *        `{ persona, levels }` object to consumers typed as an array, so
 *        `npcLevels.find(...)` threw "find is not a function" and blanked
 *        Practice as soon as a persona's levels arrived.
 *   POST /practice/npc-session      → 201, data = { session, persona, level,
 *        objectionPattern } (practiceController.createNpcSession). The web read
 *        `data.id` as the session id — undefined — so the NPC session could not
 *        be finalized.
 *
 * Each level the backend returns is flat ({ label, isPassed, attempts,
 * bestOverallScore, ... }); the web's level type names it `displayName` with an
 * optional `progress`. The mapping lives here so no page sees a raw shape.
 * Anything unrecognised becomes an empty list / null — never a value a
 * consumer can crash on.
 */

import type { LevelWithProgress, NpcProgressSummary, NpcSession } from './npcApi';

/** A level as NpcLevelService.getUserLevelProgress returns it. */
export interface ServerNpcLevel {
  id: string;
  personaId: string;
  levelKey: string;
  levelNumber: number;
  label?: string;
  displayName?: string;
  description?: string;
  minOverallScore: number;
  minClarityScore?: number;
  minConfidenceScore?: number;
  minConnectionScore?: number;
  minPaceScore?: number;
  minSessions: number;
  rewardXp: number;
  isLocked: boolean;
  isPassed?: boolean;
  bestOverallScore?: number | null;
  attempts?: number;
  passedAt?: string;
  lastAttemptAt?: string;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** `data` of a `{ success, data }` envelope (or the value itself if not enveloped). */
function envelopeData(response: unknown): unknown {
  return isObject(response) && 'data' in response ? response.data : response;
}

function toLevel(raw: unknown): LevelWithProgress | null {
  if (!isObject(raw) || typeof raw.id !== 'string') return null;
  const l = raw as unknown as ServerNpcLevel;
  const attempts = typeof l.attempts === 'number' ? l.attempts : 0;
  const passed = l.isPassed === true;
  return {
    id: l.id,
    personaId: l.personaId,
    levelKey: l.levelKey,
    levelNumber: Number(l.levelNumber),
    displayName: l.displayName ?? l.label ?? `Level ${l.levelNumber}`,
    description: l.description,
    minOverallScore: Number(l.minOverallScore),
    minClarityScore: l.minClarityScore,
    minConfidenceScore: l.minConfidenceScore,
    minPaceScore: l.minPaceScore,
    minSessions: l.minSessions,
    rewardXp: l.rewardXp,
    isLocked: l.isLocked === true,
    // No attempt yet → no progress (never a fabricated 0% best score).
    progress:
      attempts > 0 || passed
        ? {
            // Unmeasured stays null ("not measured"), never a 0.
            bestOverallScore:
              l.bestOverallScore === null || l.bestOverallScore === undefined ? null : Number(l.bestOverallScore),
            attempts,
            passed,
            passedAt: l.passedAt,
            lastAttemptAt: l.lastAttemptAt,
          }
        : undefined,
  };
}

/** GET /npc/personas/:id/levels → the persona's levels (always an array). */
export function unwrapNpcLevels(response: unknown): LevelWithProgress[] {
  const data = envelopeData(response);
  const list = Array.isArray(data) ? data : isObject(data) && Array.isArray(data.levels) ? data.levels : [];
  return list.map(toLevel).filter((l): l is LevelWithProgress => l !== null);
}

/** POST /practice/npc-session → the created session (`data.session`). */
export function unwrapNpcSession(response: unknown): NpcSession {
  const data = envelopeData(response);
  const session = isObject(data) && isObject(data.session) ? data.session : data;
  if (!isObject(session) || typeof session.id !== 'string') {
    throw new Error('NPC session response carried no session id');
  }
  return session as unknown as NpcSession;
}

/**
 * GET /npc/progress/summary → the summary NpcLevelService.getUserNpcProgressSummary
 * actually returns ({ totalPersonasAttempted, totalLevelsPassed, totalXpEarned,
 * tierBreakdown }). The web used to type it as { totalLevelsCompleted,
 * byDifficulty, masteredPersonas, totalPersonas } — fields the server never
 * sends — so /npc/progress threw on `undefined.toString()` and went blank.
 * A malformed body is null (not a summary of zeros).
 */
export function unwrapNpcProgressSummary(response: unknown): NpcProgressSummary | null {
  const data = envelopeData(response);
  if (!isObject(data)) return null;
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const totalLevelsPassed = num(data.totalLevelsPassed);
  const totalXpEarned = num(data.totalXpEarned);
  const totalPersonasAttempted = num(data.totalPersonasAttempted);
  if (totalLevelsPassed === null || totalXpEarned === null || totalPersonasAttempted === null) return null;
  return { totalPersonasAttempted, totalLevelsPassed, totalXpEarned };
}

/** Any list-valued `{ success, data: T[] }` response → T[] (never a non-array). */
export function unwrapList<T>(response: unknown): T[] {
  const data = envelopeData(response);
  return Array.isArray(data) ? (data as T[]) : [];
}

/** A value a page will call array methods on: an array, or empty. */
export function asArray<T>(value: T[] | unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}
